<script lang="ts">
	import Icon from "@components/common/Icon.svelte";
	import RequiredMark from "@components/form/RequiredMark.svelte";
	import type { ClassValue, HTMLInputAttributes } from "svelte/elements";
	import check from "@core/assets/icons/check.svg?raw";
	import { Locale, type LocalizedString } from "@core/schema/Locale.js";
	import { emailPattern } from "@core/utils/email.js";
	import { tw } from "@core/utils/svelte/tw.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	let {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		value = $bindable(),
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		group = $bindable(),
		/* eslint-disable prefer-const */
		iconClass,
		/** Renders the asterisk inside the field itself. Turn it off when a visible label already carries one. */
		requiredMark = true,
		...rest
		/* eslint-enable prefer-const */
	}: HTMLInputAttributes & { iconClass?: ClassValue; requiredMark?: boolean } = $props();
	const { tenant } = getGlobalTemplateProps();

	const type = $derived(rest.type ?? "text");
	const customPattern: Partial<Record<NonNullable<HTMLInputAttributes["type"]>, string>> = {
		email: emailPattern,
	};
	const pattern = $derived(rest.pattern ?? customPattern[type]);
	const isRadioOrCheckbox = $derived(["checkbox", "radio"].includes(String(rest.type)));
	const showRequiredMark = $derived(Boolean(rest.required) && requiredMark && !isRadioOrCheckbox);

	const customValidationMessage: Partial<Record<NonNullable<HTMLInputAttributes["type"]>, LocalizedString>> = {
		email: {
			[Locale.cs]: "E-mailová adresa",
		},
		url: {
			[Locale.cs]: "URL adresa (včetně https://)",
		},
	};

	function validate({ currentTarget }: Event & { currentTarget: EventTarget & HTMLInputElement }): void {
		currentTarget.setCustomValidity("");

		if (currentTarget.validity.valid) {
			currentTarget.setCustomValidity("");
		} else {
			const customMessage = customValidationMessage[type]?.[tenant.locale];
			if (customMessage) {
				currentTarget.setCustomValidity(customMessage);
			}
		}
	}

	let width = $state(0);

	const inputProps = $derived<HTMLInputAttributes>({
		...rest,
		class: tw(
			"min-w-0 block shadow-sm transition-[outline,color] duration-300 !outline-none border-[1.5px]",
			"text-cmctext placeholder:text-cmcmidgray font-semibold tracking-[0.02em] bg-white",
			isRadioOrCheckbox
				? "size-4 md:size-8 accent-cmcprimary cursor-pointer"
				: "focus:border-cmcprimary min-h-6 md:min-h-12 rounded-lg md:rounded-xl border-cmcmidgray text-base/3 md:text-base/tight px-2 md:px-4 py-2 md:py-3.5",
			rest.class,
			// The wrapper below becomes the flex/grid item instead of the input, so the input has to fill it on its own.
			// The padding keeps the text from running under the asterisk. Important, because tw() does not merge.
			showRequiredMark && "w-full !pr-7 md:!pr-9",
		),
		pattern,
		oninput: (event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
			validate(event);
			rest.oninput?.(event);
		},
		oninvalid: (event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
			validate(event);
			rest.oninvalid?.(event);
		},
	});
</script>

{#if type === "checkbox" || type === "radio"}
	<span class="relative inline-block">
		<input {...inputProps} bind:group {value} />
		<span class="absolute inset-0 flex items-center justify-center [:not(:checked)+&]:hidden pointer-events-none">
			<Icon icon={check} class={["size-4 md:size-8 text-cmcprimary", iconClass]} />
		</span>
	</span>
{:else if showRequiredMark}
	<span class="relative block">
		<input {...inputProps} bind:value bind:clientWidth={width} />
		<span class="absolute inset-y-0 right-2 md:right-3 flex items-center pointer-events-none">
			<RequiredMark />
		</span>
	</span>
{:else}
	<input {...inputProps} bind:value bind:clientWidth={width} />
{/if}
